import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import { needsReview } from '../lib/expenseDisplay'
import { statementBaseLabel } from '../lib/statementDisplay'
import { trackSettlementComputed, trackShareCreated } from '../lib/analytics'
import { PayButtons, PaymentHandlesEditor, EditHandlesButton } from '../components/PaymentHandles'
import {
  TrendingUp, ArrowRight, Copy, CheckCircle, Wallet,
  Users, DollarSign, AlertTriangle, ChevronDown, Loader,
  CreditCard, Check, ExternalLink, Link2, Link2Off,
  ThumbsUp, ThumbsDown, Send, Pencil, FileText, Paperclip,
} from 'lucide-react'
import clsx from 'clsx'

// ── Feedback prompt shown after settlement is computed ────────────────────────
// Shown at most once per settlement compute (local state, not persisted).
// Asking "did this look right?" right after the eureka moment maximises response rate.
function SettlementFeedback({ groupId }) {
  const [vote, setVote] = useState(null)
  const [comment, setComment] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  async function submit(selectedVote, extraComment = '') {
    setSubmitting(true)
    try {
      await api.submitFeedback(
        selectedVote === 'yes' ? 'feature' : 'bug',
        selectedVote === 'yes'
          ? 'Settlement: user confirmed the math looked correct.'
          : `Settlement: user said the math looked wrong. ${extraComment ? 'Details: ' + extraComment : ''}`,
        null,
        `/groups/${groupId}/settlement`,
      )
    } catch (e) {
      console.error('Feedback submit failed:', e)
    }
    setSubmitted(true)
    setSubmitting(false)
  }

  if (submitted) {
    return (
      <div className="mt-6 flex items-center gap-2 text-xs text-ink-500 py-2">
        <CheckCircle size={12} className="text-lime-400" />
        Thanks — your feedback helps us improve!
      </div>
    )
  }

  return (
    <div className="mt-6 pt-5 border-t border-ink-800">
      {vote === null ? (
        <div className="flex items-center gap-3">
          <span className="text-xs text-ink-500">Did the math look right?</span>
          <button
            onClick={() => { setVote('yes'); submit('yes') }}
            className="flex items-center gap-1 text-xs text-ink-400 hover:text-lime-400 transition-colors"
          >
            <ThumbsUp size={13} /> Yes
          </button>
          <button
            onClick={() => setVote('no')}
            className="flex items-center gap-1 text-xs text-ink-400 hover:text-red-400 transition-colors"
          >
            <ThumbsDown size={13} /> Something looks off
          </button>
        </div>
      ) : (
        <div className="space-y-2 animate-slide-up max-w-sm">
          <p className="text-xs text-ink-400">What looked wrong? <span className="text-ink-500">(optional)</span></p>
          <textarea
            className="input w-full text-xs resize-none h-16"
            placeholder="e.g. wrong amounts, someone missing, incorrect totals…"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <button
            onClick={() => submit('no', comment)}
            disabled={submitting}
            className="btn-secondary text-xs py-1.5"
          >
            <Send size={11} />
            {submitting ? 'Sending…' : 'Send feedback'}
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * Format an ISO date string ("2026-01-05") into "Jan 5, 2026".
 * Returns null if the value is falsy.
 */
function fmtDate(iso) {
  if (!iso) return null
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/**
 * Format a statement period into something readable.
 * e.g. "Jan 5 – Apr 14, 2026"  or  "Imported Apr 14, 2026"  or  "Unknown period"
 */
function fmtPeriod(stmt) {
  if (stmt.period_start && stmt.period_end) {
    const start = fmtDate(stmt.period_start)
    const end = fmtDate(stmt.period_end)
    return `${start} – ${end}`
  }
  if (stmt.statement_date) {
    return `Statement date: ${fmtDate(stmt.statement_date)}`
  }
  return 'No date info'
}

// Currency symbol map — same as TransactionsPage
const CURRENCY_SYMBOLS = {
  USD: '$', AUD: 'A$', NZD: 'NZ$', JPY: '¥',
  GBP: '£', EUR: '€', CAD: 'C$', SGD: 'S$', HKD: 'HK$', THB: '฿', MXN: 'Mex$',
}

function formatAmount(amount, currency = 'USD') {
  const sym = CURRENCY_SYMBOLS[currency] || currency + ' '
  const decimals = currency === 'JPY' ? 0 : 2
  return `${sym}${Math.abs(amount).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`
}

// ── Copy to clipboard with visual feedback ──────────────────────────────────
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false)

  // useEffect handles the "copied → reset after 2s" transition. Putting the
  // timeout here instead of inside copy() means it gets cleaned up when the
  // component unmounts (e.g. user navigates away mid-toast) — otherwise
  // React fires "setState on unmounted component" warnings.
  useEffect(() => {
    if (!copied) return
    const id = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(id)
  }, [copied])

  const copy = () => {
    navigator.clipboard.writeText(text)
    setCopied(true)
  }

  return (
    <button
      onClick={copy}
      aria-label={copied ? 'Copied to clipboard' : 'Copy to clipboard'}
      className={clsx(
        'btn-ghost py-1 px-2 text-xs transition-colors',
        copied && 'text-lime-400'
      )}
    >
      {copied ? <CheckCircle size={12} /> : <Copy size={12} />}
      {copied ? 'Copied!' : 'Copy'}
    </button>
  )
}

// ── Balance card for one member ──────────────────────────────────────────────
function BalanceCard({ balance, index, currency = 'USD' }) {
  const isOwed = balance.balance >= 0
  const isZero = Math.abs(balance.balance) < 0.01
  const initials = balance.member_name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
  const colors = [
    'bg-lime-400 text-ink-950',
    'bg-green-400 text-ink-950',
    'bg-amber-400 text-ink-950',
    'bg-red-400 text-white',
    'bg-blue-400 text-white',
  ]

  return (
    <div className="card-sm flex items-center gap-4 animate-slide-up" style={{ animationDelay: `${index * 80}ms` }}>
      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${colors[index % colors.length]}`}>
        {initials}
      </div>
      <div className="flex-1">
        <div className="font-medium text-ink-100">{balance.member_name}</div>
        <div className={clsx(
          'text-xs font-mono mt-0.5',
          isZero ? 'text-ink-400' : isOwed ? 'text-green-400' : 'text-red-400'
        )}>
          {isZero
            ? 'Settled up'
            : isOwed
              ? `Is owed ${formatAmount(balance.balance, currency)}`
              : `Owes ${formatAmount(balance.balance, currency)}`}
        </div>
      </div>
      <div className={clsx(
        'font-mono text-xl font-bold',
        isZero ? 'text-ink-400' : isOwed ? 'text-green-400' : 'text-red-400'
      )}>
        {isZero ? '—' : `${isOwed ? '+' : '–'}${formatAmount(balance.balance, currency)}`}
      </div>
    </div>
  )
}

// ── Transfer card ─────────────────────────────────────────────────────────────
// groupId is needed to build a unique localStorage key per trip.
// This prevents "paid" state from bleeding across different trips.
function TransferCard({ transfer, index, currency = 'USD', groupId, members = [], onEditHandles }) {
  const [expanded, setExpanded] = useState(false)

  // The payee (recipient) is the one whose handles we use to render Pay buttons.
  // We find the full member object so we have access to .payment_handles.
  const payee = members.find((m) => m.id === transfer.to_member_id)

  // Build a stable localStorage key for this specific transfer within this group.
  // We use names + amount so the key is human-readable and unique within the group.
  const paidKey = `autosplit_paid_${groupId}_${transfer.from_member_name}_${transfer.to_member_name}`

  // Read initial paid state from localStorage so it survives page refresh.
  const [paid, setPaid] = useState(() => localStorage.getItem(paidKey) === 'true')

  function togglePaid() {
    const next = !paid
    setPaid(next)
    // Persist to localStorage — 'true' string or remove the key
    if (next) {
      localStorage.setItem(paidKey, 'true')
    } else {
      localStorage.removeItem(paidKey)
    }
  }

  return (
    <div
      className={clsx(
        'card-sm animate-slide-up transition-opacity',
        paid && 'opacity-50'   // Fade out paid transfers — they're done
      )}
      style={{ animationDelay: `${index * 80}ms` }}
    >
      <div className="flex items-center gap-3">
        <div className="flex-1 flex items-center gap-2 min-w-0">
          {/* Names — strikethrough when paid */}
          <span className={clsx('font-medium text-ink-100 truncate', paid && 'line-through text-ink-500')}>
            {transfer.from_member_name}
          </span>
          <ArrowRight size={14} className="text-ink-500 flex-shrink-0" />
          <span className={clsx('font-medium text-ink-100 truncate', paid && 'line-through text-ink-500')}>
            {transfer.to_member_name}
          </span>
        </div>

        <div className={clsx('font-mono text-xl font-bold flex-shrink-0', paid ? 'text-ink-500 line-through' : 'text-lime-400')}>
          {formatAmount(transfer.amount, currency)}
        </div>

        {/* Mark as paid toggle — the key organizer feature */}
        <button
          onClick={togglePaid}
          title={paid ? 'Mark as unpaid' : 'Mark as paid'}
          aria-label={paid ? 'Mark as unpaid' : 'Mark as paid'}
          aria-pressed={paid}
          className={clsx(
            'flex-shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all',
            paid
              ? 'bg-green-400 border-green-400 text-ink-950'           // Filled green when paid
              : 'bg-transparent border-ink-600 text-transparent hover:border-green-400'  // Empty circle when unpaid
          )}
        >
          <Check size={12} strokeWidth={3} />
        </button>

        {/* Expand/collapse payment message */}
        <button
          className="btn-ghost py-1 px-2 flex-shrink-0"
          onClick={() => setExpanded(!expanded)}
          title="Payment request message"
          aria-label={expanded ? 'Hide payment message' : 'Show payment message'}
          aria-expanded={expanded}
        >
          <ChevronDown size={14} className={clsx('transition-transform', expanded && 'rotate-180')} />
        </button>
      </div>

      {/* "Paid" label — shows when marked as paid */}
      {paid && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-green-400 font-medium">
          <CheckCircle size={11} />
          Marked as paid · <button onClick={togglePaid} className="underline hover:text-green-300 transition-colors">Undo</button>
        </div>
      )}

      {/* One-tap pay buttons — opens Venmo / Cash App / PayPal pre-filled.
          Hidden once marked paid (no need to pay again). */}
      {!paid && (
        <div className="mt-3 pt-3 border-t border-ink-800 flex items-center justify-between gap-2 flex-wrap">
          <PayButtons
            payee={payee}
            amount={transfer.amount}
            memo={`AutoSplit: ${transfer.from_member_name} → ${transfer.to_member_name}`}
          />
          {payee && onEditHandles && (
            <EditHandlesButton
              member={payee}
              onClick={() => onEditHandles(payee)}
            />
          )}
        </div>
      )}

      {expanded && (
        <div className="mt-3 pt-3 border-t border-ink-700 animate-slide-up">
          <div className="text-xs text-ink-400 mb-2 font-medium">Copyable payment request:</div>
          <div className="bg-ink-900 rounded-lg p-3 text-sm text-ink-200 leading-relaxed font-mono text-xs border border-ink-700">
            {transfer.payment_request}
          </div>
          <div className="flex gap-2 mt-2">
            <CopyButton text={transfer.payment_request} />
          </div>
        </div>
      )}
    </div>
  )
}

// ── Card holders summary ──────────────────────────────────────────────────────
// Shows who holds which card, with a warning for statements missing a card holder.
// This is the key UI for multi-card trips — replaces the confusing single dropdown.

function CardHoldersSummary({ statements, members, groupId }) {
  const qc = useQueryClient()

  // Build a name lookup: member_id → name
  const memberNames = Object.fromEntries(members.map(m => [m.id, m.name]))

  // Inline rename: which statement's name is being edited, and its draft text.
  const [renamingId, setRenamingId] = useState(null)
  const [renameDraft, setRenameDraft] = useState('')
  const rename = useMutation({
    mutationFn: ({ statementId, nickname }) => api.renameStatement(statementId, nickname),
    onSuccess: () => {
      qc.invalidateQueries(['statements', groupId])
      setRenamingId(null)
    },
  })

  // Open the original uploaded document in a new tab. It's fetched with the
  // auth token (a bare link can't carry it) and shown via a blob URL.
  const viewOriginal = async (statementId) => {
    const blob = await api.fetchStatementOriginal(statementId)
    window.open(URL.createObjectURL(blob), '_blank', 'noopener,noreferrer')
  }

  // Attach the source document to a statement imported before we stored
  // originals — the user still has the file on disk.
  const attach = useMutation({
    mutationFn: ({ statementId, file }) => api.attachStatementOriginal(statementId, file),
    onSuccess: () => qc.invalidateQueries(['statements', groupId]),
  })

  // Assign a card holder right here on the Settle page. The old "Fix in
  // Upload" button navigated to a legacy route that just bounced back to the
  // expenses screen — a dead end (user-reported: "it keeps telling me to go
  // back", with 7 statements stuck in amber).
  const setHolder = useMutation({
    mutationFn: ({ statementId, memberId }) => api.setCardHolder(statementId, memberId),
    onSuccess: () => {
      qc.invalidateQueries(['statements', groupId])
      qc.invalidateQueries(['group-transactions', groupId])
    },
  })

  return (
    <div className="space-y-2">
      {statements.map(stmt => {
        const holderName = stmt.card_holder_member_id
          ? memberNames[stmt.card_holder_member_id]
          : null

        // Format the statement period nicely: "Mar 15 – Apr 14"
        const period = fmtPeriod(stmt)

        return (
          <div
            key={stmt.id}
            className={clsx(
              'flex items-center gap-3 px-3 py-2.5 rounded-lg border',
              holderName
                ? 'bg-ink-800/40 border-ink-700'
                : 'bg-amber-400/5 border-amber-400/25'
            )}
          >
            {/* Status icon */}
            {holderName
              ? <Check size={14} className="text-lime-400 flex-shrink-0" />
              : <AlertTriangle size={14} className="text-amber-400 flex-shrink-0" />
            }

            {/* Card info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {renamingId === stmt.id ? (
                  /* Inline rename — Enter/blur saves, Escape cancels.
                     "" clears back to the automatic "Bank ···last4" label. */
                  <input
                    className="input text-sm py-0.5 px-1.5 h-7 w-52"
                    value={renameDraft}
                    autoFocus
                    maxLength={100}
                    placeholder="e.g. Sapphire (trip card)"
                    onChange={(e) => setRenameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') rename.mutate({ statementId: stmt.id, nickname: renameDraft.trim() })
                      if (e.key === 'Escape') setRenamingId(null)
                    }}
                    onBlur={() => rename.mutate({ statementId: stmt.id, nickname: renameDraft.trim() })}
                  />
                ) : (
                  <span className={clsx(
                    'text-sm font-medium',
                    holderName ? 'text-ink-100' : 'text-amber-400'
                  )}>
                    {/* Nickname wins; else "Alex's Chase ···1455" — the last-4
                        is what tells three same-bank cards apart (real bug:
                        seven identical "elle's Chase" rows). */}
                    {stmt.nickname
                      ? stmt.nickname
                      : holderName
                        ? `${holderName}'s ${statementBaseLabel(stmt) || 'Card'}`
                        : statementBaseLabel(stmt) || 'No card holder set'
                    }
                  </span>
                )}
                {period && (
                  <>
                    <span className="text-xs text-ink-500">·</span>
                    <span className="text-xs text-ink-500 font-mono truncate">{period}</span>
                  </>
                )}
                {/* Row tools: rename always; view-original only when the
                    uploaded document is actually stored (legacy rows aren't). */}
                <button
                  className="p-1 rounded text-ink-500 hover:text-lime-400 transition-colors"
                  title="Rename this statement"
                  onClick={() => {
                    setRenameDraft(stmt.nickname || '')
                    setRenamingId(stmt.id)
                  }}
                >
                  <Pencil size={11} />
                </button>
                {stmt.has_original ? (
                  <button
                    className="p-1 rounded text-ink-500 hover:text-lime-400 transition-colors"
                    title="View original document"
                    onClick={() => viewOriginal(stmt.id)}
                  >
                    <FileText size={11} />
                  </button>
                ) : (
                  /* No stored document (imported before we kept originals) —
                     let the user attach the file they still have on disk.
                     A <label> wrapping a hidden input is the accessible way
                     to style a file picker. */
                  <label
                    className="p-1 rounded text-ink-500 hover:text-lime-400 transition-colors cursor-pointer"
                    title="Attach the original document"
                  >
                    <Paperclip size={11} />
                    <input
                      type="file"
                      accept=".pdf,.csv,image/*"
                      className="sr-only"
                      data-testid={`attach-original-input-${stmt.id}`}
                      disabled={attach.isPending}
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) attach.mutate({ statementId: stmt.id, file })
                      }}
                    />
                  </label>
                )}
              </div>
              <div className="text-xs text-ink-500 mt-0.5">
                {stmt.transaction_count} transaction{stmt.transaction_count !== 1 ? 's' : ''}
              </div>
            </div>

            {/* Inline card-holder picker — assigned statements can be
                corrected, unassigned ones fixed on the spot. No detours. */}
            <select
              className={clsx(
                'select text-xs py-1 pl-2 pr-6 h-7 w-auto flex-shrink-0',
                !holderName && 'border-amber-400/40 text-amber-300'
              )}
              value={stmt.card_holder_member_id || ''}
              disabled={setHolder.isPending}
              onChange={(e) => {
                if (!e.target.value) return
                setHolder.mutate({ statementId: stmt.id, memberId: parseInt(e.target.value) })
              }}
              aria-label={`Card holder for ${stmt.bank_name || 'statement'}`}
            >
              <option value="" disabled>Whose card?</option>
              {members.filter(m => !m.is_placeholder).map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
        )
      })}
    </div>
  )
}

// ── Share trip section ───────────────────────────────────────────────────────
// Full-width CTA placed AFTER the settlement results — the natural "you're done,
// now tell everyone" moment. Creates a public read-only link anyone can open.
function SharePanel({ groupId, effectivePayerId }) {
  const [shareUrl, setShareUrl] = useState(null)
  const [copied, setCopied] = useState(false)
  const [loading, setLoading] = useState(false)
  const [revoking, setRevoking] = useState(false)

  // Reset the "copied!" indicator on a timer; cleanup prevents setState on
  // an unmounted component if the user navigates away mid-toast.
  useEffect(() => {
    if (!copied) return
    const id = setTimeout(() => setCopied(false), 2500)
    return () => clearTimeout(id)
  }, [copied])

  async function handleShare() {
    setLoading(true)
    try {
      const result = await api.createShare(groupId, effectivePayerId)
      // Build the share URL from the current browser origin rather than
      // trusting result.share_url. The backend builds it from FRONTEND_URL
      // which can drift to a Vercel preview domain — same fix as the
      // PeopleSheet invite link. The share_code is canonical.
      setShareUrl(`${window.location.origin}/share/${result.share_code}`)
      // Track the share creation — this is the key viral moment
      trackShareCreated(groupId)
    } catch (e) {
      console.error('Share failed:', e)
    } finally {
      setLoading(false)
    }
  }

  async function handleRevoke() {
    setRevoking(true)
    try {
      await api.revokeShare(groupId)
      setShareUrl(null)
    } catch (e) {
      console.error('Revoke failed:', e)
    } finally {
      setRevoking(false)
    }
  }

  function copyLink() {
    navigator.clipboard.writeText(shareUrl)
    setCopied(true)
    // The reset timeout lives in the useEffect above, with proper cleanup.
  }

  // Helper: open a pre-filled iMessage / WhatsApp / plain SMS with the share link
  function openMessaging(platform) {
    const text = `Here's our trip settlement — tap to see what you owe: ${shareUrl}`
    if (platform === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank')
    } else if (platform === 'imessage') {
      window.open(`sms:&body=${encodeURIComponent(text)}`, '_blank')
    }
  }

  // ── Before link is generated: invite-style CTA card ─────────────────────
  if (!shareUrl) {
    return (
      <div className="mt-8 rounded-2xl border border-dashed border-ink-600 bg-ink-900/50 px-6 py-7 text-center">
        <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-lime-400/10 border border-lime-400/20 mb-4">
          <Link2 size={18} className="text-lime-400" strokeWidth={1.5} />
        </div>
        <h3 className="font-display text-lg font-semibold text-ink-100 mb-1">Share with your group</h3>
        <p className="text-sm text-ink-400 mb-5 max-w-sm mx-auto leading-relaxed">
          Generate a link your friends can open to see the full breakdown — no sign-up required.
        </p>
        <button
          onClick={handleShare}
          disabled={loading}
          className="btn-primary gap-2 mx-auto"
        >
          {loading ? <Loader size={14} className="animate-spin" /> : <Link2 size={14} />}
          {loading ? 'Generating link…' : 'Generate share link'}
        </button>
      </div>
    )
  }

  // ── After link is generated: show URL + quick-send shortcuts ────────────
  return (
    <div className="mt-8 rounded-2xl border border-lime-400/20 bg-lime-400/5 px-6 py-6">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <div className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-lime-400/15">
          <Link2 size={13} className="text-lime-400" />
        </div>
        <h3 className="font-display text-base font-semibold text-ink-100">Share link ready</h3>
        <span className="ml-auto text-[10px] text-lime-400/70 font-mono tracking-wider uppercase">Live</span>
      </div>

      {/* URL bar */}
      <div className="flex items-center gap-2 bg-ink-900 border border-ink-700 rounded-xl px-3 py-2.5 mb-4">
        <span className="text-xs text-ink-300 font-mono truncate flex-1 min-w-0">
          {shareUrl.replace(/^https?:\/\//, '')}
        </span>
        <button
          onClick={copyLink}
          className={clsx(
            'flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-all',
            copied
              ? 'bg-lime-400/15 text-lime-400'
              : 'bg-ink-700 hover:bg-ink-600 text-ink-300 hover:text-ink-100'
          )}
        >
          {copied ? <CheckCircle size={11} /> : <Copy size={11} />}
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>

      {/* Quick-send shortcuts */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-ink-500 mr-1">Send via:</span>
        {/* WhatsApp */}
        <button
          onClick={() => openMessaging('whatsapp')}
          className="flex items-center gap-1.5 text-xs text-ink-400 hover:text-ink-200
                     bg-ink-800 hover:bg-ink-700 border border-ink-700 hover:border-ink-500
                     px-3 py-1.5 rounded-lg transition-all"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" className="text-green-400">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          WhatsApp
        </button>
        {/* iMessage / SMS */}
        <button
          onClick={() => openMessaging('imessage')}
          className="flex items-center gap-1.5 text-xs text-ink-400 hover:text-ink-200
                     bg-ink-800 hover:bg-ink-700 border border-ink-700 hover:border-ink-500
                     px-3 py-1.5 rounded-lg transition-all"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" className="text-blue-400">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
          </svg>
          iMessage
        </button>
        {/* Revoke — tucked at the end, low prominence */}
        <button
          onClick={handleRevoke}
          disabled={revoking}
          className="ml-auto flex items-center gap-1.5 text-xs text-ink-500 hover:text-red-400 transition-colors"
          title="Disable this link"
        >
          <Link2Off size={11} />
          {revoking ? 'Revoking…' : 'Revoke'}
        </button>
      </div>
    </div>
  )
}


// ── Main page ─────────────────────────────────────────────────────────────────
export default function SettlementPage() {
  const { groupId } = useParams()
  const navigate = useNavigate()

  // fallbackPayerId: only used when some statements have no card holder.
  // If all statements are assigned, the backend ignores this value —
  // each transaction is credited to its statement's card holder directly.
  const [fallbackPayerId, setFallbackPayerId] = useState('')

  // Optional: filter settlement to just one statement
  const [statementId, setStatementId] = useState('')

  const [settlement, setSettlement] = useState(null)
  const [error, setError] = useState(null)
  // Snapshot how many transactions existed at the time of the last computation.
  // If new transactions are added afterwards, we show a "stale" warning.
  const [txnCountAtCompute, setTxnCountAtCompute] = useState(null)

  // Which member's payment handles is the user editing? null = editor closed.
  // Triggered from any TransferCard (recipient) or directly from the toolbar.
  const [editingHandlesFor, setEditingHandlesFor] = useState(null)

  const { data: group } = useQuery({
    queryKey: ['group', groupId],
    queryFn: () => api.getGroup(groupId),
  })

  const { data: allStatements = [], isSuccess: statementsLoaded } = useQuery({
    queryKey: ['statements', groupId],
    queryFn: () => api.getStatements(groupId),
  })

  // Fetch transactions so we can warn if any still need participant assignment
  const { data: allTransactions = [], isSuccess: transactionsLoaded } = useQuery({
    queryKey: ['group-transactions', groupId],
    queryFn: () => api.getGroupTransactions(groupId),
  })

  // Both lists must be loaded before we classify the trip (manual vs statement)
  // or auto-run the math. If transactions arrive before statements, a statement
  // trip would briefly look "manual-only" and we'd compute with wrong payers.
  const dataLoaded = statementsLoaded && transactionsLoaded

  // Count transactions that are genuinely unresolved — uses the SAME shared
  // predicate as the Expenses screen so the two pages can never disagree
  // about how many expenses "need a quick look" (excluded rows don't count).
  const needsReviewCount = allTransactions.filter(needsReview).length

  const members = group?.members || []
  const currency = group?.base_currency || 'USD'

  // ── Classify statements ────────────────────────────────────────────────────
  // The backend now sets is_manual=true for virtual "Manual Expenses" containers.
  // These are not real uploaded files — they're auto-created to hold manual entries.
  // We hide them from the settlement UI because they always have a card_holder set
  // (the payer) and never need configuration from the user.
  const realStatements = allStatements.filter(s => !s.is_manual)

  // Are all real statements fully configured with a card holder?
  const allAssigned = realStatements.length > 0 && realStatements.every(s => s.card_holder_member_id)

  // How many real statements are missing a card holder?
  const unassignedCount = realStatements.filter(s => !s.card_holder_member_id).length

  // Manual-only trip: zero uploaded statements, all expenses entered manually
  // (which means each transaction already knows its payer via paid_by_member_id).
  // Settlement doesn't need a fallback in this case.
  const manualOnly = realStatements.length === 0 && allTransactions.length > 0

  // ── Effective payer for backend ────────────────────────────────────────────
  // If every statement has a card holder set OR we're manual-only, the backend's
  // `payer_member_id` param is never actually used. We still have to send
  // *something* — auto-pick the first member.
  const effectivePayerId = (allAssigned || manualOnly)
    ? (members[0]?.id ?? '')
    : parseInt(fallbackPayerId)

  // Can we compute settlement?
  // Yes if: there are members AND there's something to settle AND
  //         (all statements assigned OR manual-only OR user picked a fallback)
  const canCompute =
    members.length > 0 &&
    allTransactions.length > 0 &&
    (allAssigned || manualOnly || !!fallbackPayerId)

  const compute = useMutation({
    mutationFn: () => api.computeSettlement(
      groupId,
      effectivePayerId,
      statementId ? parseInt(statementId) : null
    ),
    onSuccess: (data) => {
      setSettlement(data)
      setError(null)
      // Snapshot the transaction count so we can warn if more are added later
      setTxnCountAtCompute(allTransactions.length)
      // Track the settlement compute event — useful for understanding usage depth
      trackSettlementComputed({
        memberCount: members.length,
        transferCount: data.transfers?.length ?? 0,
        totalAmount: data.transfers?.reduce((s, t) => s + t.amount, 0) ?? 0,
      })
    },
    onError: (err) => setError(err.message),
  })

  const totalOwed = settlement?.transfers.reduce((s, t) => s + t.amount, 0) || 0

  // True when new transactions have been added after the last computation.
  // This means the settlement results may no longer be accurate.
  const settlementStale = settlement !== null && txnCountAtCompute !== null && allTransactions.length !== txnCountAtCompute

  // ── Auto-compute ────────────────────────────────────────────────────────────
  // The user shouldn't have to press a button to see math the app can already
  // do. As soon as everything needed is loaded (and again if results go stale
  // because expenses changed), run the settlement automatically. The `error`
  // guard stops an infinite retry loop if the backend rejects the request —
  // the user then sees the error card and nothing re-fires.
  useEffect(() => {
    const needsRun = settlement === null || settlementStale
    if (!dataLoaded || !canCompute || !needsRun || compute.isPending || error) return
    compute.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoaded, canCompute, settlementStale, settlement, error])

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-display text-2xl md:text-3xl font-semibold text-ink-50 tracking-tight">Settle up</h1>
        <p className="text-ink-400 text-sm mt-1">
          Who pays whom — worked out automatically
        </p>
      </div>

      {/* ── Configuration card ──────────────────────────────────────────────── */}
      {/* Only matters when bank statements were uploaded (each statement needs
          an owner). Manual-only trips already know who paid each expense, so
          this whole card is noise — hide it and let auto-compute run. Waiting
          for dataLoaded also stops the card flashing in before classification. */}
      {dataLoaded && !manualOnly && (
      <div className="card mb-6">
        <h2 className="font-display text-lg font-semibold text-ink-100 mb-4 flex items-center gap-2">
          <CreditCard size={16} className="text-ink-400" />
          Card Holders
        </h2>

        {realStatements.length === 0 ? (
          // No statements and no manual entries either (manual-only trips hide
          // this whole card) — the trip is simply empty.
          <div className="text-center py-6">
            <p className="text-sm text-ink-400 mb-3">No expenses yet</p>
            <button
              className="btn-secondary text-xs"
              onClick={() => navigate(`/groups/${groupId}`)}
            >
              Add expenses
            </button>
          </div>
        ) : (
          <>
            {/*
              Show who holds which card.
              Green = card holder set (settlement will credit them automatically).
              Amber = no card holder set (will fall through to the fallback payer below).
            */}
            <CardHoldersSummary
              statements={realStatements}
              members={members}
              groupId={groupId}
            />

            {/* Explanation of how multi-card settlement works */}
            <div className="mt-3 px-3 py-2.5 rounded-lg bg-ink-800/60 border border-ink-700">
              <p className="text-xs text-ink-400 leading-relaxed">
                {allAssigned
                  ? `Each person will be credited for their own card's charges. No further setup needed — the math runs automatically.`
                  : `${unassignedCount} statement${unassignedCount !== 1 ? 's' : ''} ${unassignedCount !== 1 ? 'are' : 'is'} missing a card holder — pick whose card each one is with the dropdown on the amber rows above. (Or choose a fallback payer below to attribute them all to one person.)`
                }
              </p>
            </div>
          </>
        )}

        {/*
          Fallback payer — only needed when some statements have no card holder.
          If all statements are assigned, this section is hidden entirely.
        */}
        {!allAssigned && realStatements.length > 0 && (
          <div className="mt-4 pt-4 border-t border-ink-800">
            <label className="label">
              Fallback: who paid for unassigned statements?
            </label>
            <p className="text-xs text-ink-500 mb-2">
              Transactions from statements without a card holder will be credited to this person.
            </p>
            <select
              className="select"
              value={fallbackPayerId}
              onChange={(e) => setFallbackPayerId(e.target.value)}
            >
              <option value="">Select fallback payer…</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
        )}

        {/*
          Optional: filter to a single statement.
          Only shown when there are multiple real statements to choose from.
        */}
        {realStatements.length > 1 && (
          <div className="mt-4">
            <label className="label">
              Filter by statement <span className="text-ink-500 normal-case font-normal">(optional)</span>
            </label>
            <select
              className="select"
              value={statementId}
              onChange={(e) => setStatementId(e.target.value)}
            >
              <option value="">All statements (recommended)</option>
              {realStatements.map((s) => {
                const holder = members.find(m => m.id === s.card_holder_member_id)?.name
                return (
                  <option key={s.id} value={s.id}>
                    {holder ? `${holder}'s card` : 'Unassigned'} — {fmtPeriod(s)} ({s.transaction_count} txns)
                  </option>
                )
              })}
            </select>
          </div>
        )}

        {/* Calculate button */}
        <div className="mt-5 flex items-center gap-3">
          <button
            className="btn-primary"
            onClick={() => compute.mutate()}
            disabled={!canCompute || compute.isPending}
          >
            {compute.isPending
              ? <><Loader size={14} className="animate-spin" /> Calculating…</>
              : <><TrendingUp size={14} /> Calculate</>
            }
          </button>
          {!canCompute && realStatements.length > 0 && (
            <span className="text-xs text-amber-400">
              Select a fallback payer to continue
            </span>
          )}
        </div>
      </div>
      )}

      {/* Error card lives OUTSIDE the configuration card so a failed
          auto-compute is still visible on manual-only trips (where the
          configuration card above is hidden entirely). */}
      {error && (
        <div className="mb-6 flex gap-2 items-start px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-400">
          <AlertTriangle size={15} className="flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium mb-0.5">Settlement failed</div>
            <div className="text-xs text-red-400/80">{error}</div>
          </div>
          {/* Without this, a single network blip would leave manual-only trips
              stuck: their Calculate button lives in the hidden config card. */}
          <button
            className="btn-secondary text-xs flex-shrink-0"
            onClick={() => { setError(null); compute.mutate() }}
            disabled={compute.isPending}
          >
            Try again
          </button>
        </div>
      )}

      {/* ── Stale settlement warning ────────────────────────────────────────── */}
      {/* Shown when transactions were added or changed after the last settlement compute */}
      {settlementStale && (
        <div className="mb-4 flex items-start gap-2.5 px-4 py-3 rounded-xl bg-amber-400/8 border border-amber-400/25 animate-slide-up">
          <AlertTriangle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <span className="text-sm font-medium text-amber-400">Transactions changed since last computation</span>
            <span className="text-xs text-ink-400 ml-2">Recompute to get updated results.</span>
          </div>
        </div>
      )}

      {/* ── Results ─────────────────────────────────────────────────────────── */}
      {settlement && (
        <div className="animate-slide-up">
          {/* Summary bar */}
          <div className="card mb-6 bg-gradient-to-r from-ink-900 to-ink-800 border-ink-600">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-xs text-ink-400 mb-1">Total shared</p>
                <p className="font-mono text-3xl font-bold text-lime-400">
                  {formatAmount(settlement.total_shared_expenses, currency)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-ink-400 mb-1">Transfers needed</p>
                <p className="font-mono text-3xl font-bold text-ink-100">
                  {settlement.transfers.length}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-ink-400 mb-1">Changing hands</p>
                <p className="font-mono text-3xl font-bold text-amber-400">
                  {formatAmount(totalOwed, currency)}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Balances */}
            <div>
              <h2 className="font-display text-xl font-semibold text-ink-100 mb-3 flex items-center gap-2">
                <Users size={16} className="text-ink-400" />
                Where everyone stands
              </h2>
              <div className="space-y-2">
                {settlement.balances.map((b, i) => (
                  <BalanceCard key={b.member_id} balance={b} index={i} currency={currency} />
                ))}
              </div>
            </div>

            {/* Transfers */}
            <div>
              <h2 className="font-display text-xl font-semibold text-ink-100 mb-3 flex items-center gap-2">
                <DollarSign size={16} className="text-ink-400" />
                Who pays whom
              </h2>
              {settlement.transfers.length === 0 ? (
                <div className="card-sm text-center py-6">
                  <CheckCircle size={24} className="text-green-400 mx-auto mb-2" />
                  <p className="text-sm text-ink-300">All settled up!</p>
                  <p className="text-xs text-ink-500 mt-1">No transfers needed</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {settlement.transfers.map((t, i) => (
                    // Stable key derived from the transfer's identity (who pays
                    // whom + amount), NOT the array index. Array indices break
                    // local state like "mark as paid" when the list reorders —
                    // React thinks "the second card is the same card" even
                    // when its content changed, so the paid flag follows the
                    // wrong transfer.
                    <TransferCard
                      key={`${t.from_member_id}-${t.to_member_id}-${t.amount}`}
                      transfer={t}
                      index={i}
                      currency={currency}
                      groupId={groupId}
                      members={members}
                      onEditHandles={setEditingHandlesFor}
                    />
                  ))}
                </div>
              )}

              {settlement.transfers.length > 0 && (
                <div className="mt-4 flex items-center gap-3 flex-wrap">
                  <CopyButton text={settlement.transfers.map(t => t.payment_request).join('\n\n')} />
                  <span className="text-xs text-ink-500">Copy all messages</span>
                  <span className="text-ink-700">·</span>
                  {/* Quick entry to manage YOUR handles — even if no transfer is owed to you,
                      you might want to add yours so the next trip already has it. */}
                  <button
                    className="flex items-center gap-1.5 text-xs text-ink-400 hover:text-lime-400 transition-colors"
                    onClick={() => {
                      // Default to the first member that's "self" if we can detect it,
                      // otherwise fall back to opening the first member.
                      const me = members[0]
                      if (me) setEditingHandlesFor(me)
                    }}
                  >
                    <Wallet size={12} />
                    Manage payment handles
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Warning: unresolved "needs review" transactions affect accuracy */}
          {needsReviewCount > 0 && (
            <div className="mt-6 card-sm border-amber-400/20 bg-amber-400/5 flex gap-2">
              <AlertTriangle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-ink-300">
                <span className="font-semibold text-amber-400">{needsReviewCount} expense{needsReviewCount !== 1 ? 's' : ''} still need{needsReviewCount === 1 ? 's' : ''} a quick look</span>
                {' '}— {needsReviewCount === 1 ? "it's" : "they're"} left out of this settlement until you say who splits {needsReviewCount === 1 ? 'it' : 'them'}.{' '}
                <button
                  className="text-lime-400 underline"
                  onClick={() => navigate(`/groups/${groupId}`)}
                >
                  Review on the Expenses screen
                </button>
                .
              </p>
            </div>
          )}

          {/* Share trip — natural moment: user just saw all the owed amounts */}
          <SharePanel groupId={groupId} effectivePayerId={effectivePayerId} />

          {/* Contextual feedback prompt — did the math look right? */}
          <SettlementFeedback groupId={groupId} />
        </div>
      )}

      {/* Empty state — suppressed while an error card is showing, so the user
          isn't told we're "crunching" when we've actually stopped. */}
      {!settlement && !compute.isPending && !error && (
        <div className="card text-center py-12">
          <TrendingUp size={36} className="text-ink-500 mx-auto mb-3" />
          <p className="font-display text-xl text-ink-300 mb-2">Ready to settle up</p>
          <p className="text-sm text-ink-500">
            {allTransactions.length === 0
              ? 'Add an expense first — upload a statement, snap a receipt, or type one in'
              : canCompute
                ? 'Crunching the numbers…'
                : 'Choose who owns each statement above, then tap Calculate'
            }
          </p>
        </div>
      )}

      {/* Payment handles editor — opened from any TransferCard or the toolbar.
          Self-contained sheet that mutates the member, then closes. */}
      {editingHandlesFor && (
        <PaymentHandlesEditor
          member={editingHandlesFor}
          onClose={() => setEditingHandlesFor(null)}
        />
      )}
    </div>
  )
}
